import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  PlayArrow as RunIcon,
  AutoAwesome as AIIcon,
  Email as EmailIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { localGenerateContent } from '../../api/local-generate-content';
import { localSendNewsletter } from '../../api/local-send-newsletter';

interface CronJob {
  id: string;
  name: string;
  description: string;
  endpoint: string;
  icon: React.ReactNode;
  color: string;
}

const cronJobs: CronJob[] = [
  {
    id: 'generate-content',
    name: 'Generate Article with AI',
    description: 'Generate a new article using OpenAI GPT-4. The article will be saved as a draft.',
    endpoint: '/api/generate-content',
    icon: <AIIcon />,
    color: '#9c27b0',
  },
  {
    id: 'send-newsletter',
    name: 'Send Newsletter',
    description: 'Generate and send the daily newsletter to all active subscribers.',
    endpoint: '/api/send-newsletter',
    icon: <EmailIcon />,
    color: '#ed6c02',
  },
];

const CronJobsManager: React.FC = () => {
  const [loading, setLoading] = useState<string | null>(null);
  const [results, setResults] = useState<{ [key: string]: { success: boolean; message: string } }>({});

  const handleRunCronJob = async (job: CronJob) => {
    setLoading(job.id);
    setResults((prev) => ({ ...prev, [job.id]: { success: false, message: '' } }));

    try {
      // Check if we're in development mode
      const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
      
      if (isDevelopment) {
        // Use local testing functions
        let result: { success: boolean; message: string };
        if (job.id === 'generate-content') {
          result = await localGenerateContent();
        } else if (job.id === 'send-newsletter') {
          result = await localSendNewsletter();
        } else {
          throw new Error('Unknown job type');
        }

        setResults((prev) => ({
          ...prev,
          [job.id]: {
            success: result.success,
            message: result.message,
          },
        }));
      } else {
        // Production mode - use actual API endpoints
        const cronSecret = process.env.REACT_APP_CRON_SECRET || '';
        
        if (!cronSecret) {
          throw new Error('CRON_SECRET not configured. Please set REACT_APP_CRON_SECRET in your environment variables.');
        }

        const response = await fetch(job.endpoint, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${cronSecret}`,
            'Content-Type': 'application/json',
          },
        });

        // Check if response is JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await response.text();
          throw new Error(`Server returned non-JSON response (${response.status}): ${text.substring(0, 200)}`);
        }

        const data = await response.json();

        if (response.ok) {
          setResults((prev) => ({
            ...prev,
            [job.id]: {
              success: true,
              message: data.message || 'Cron job executed successfully!',
            },
          }));
        } else {
          throw new Error(data.error || `Failed to execute cron job (${response.status})`);
        }
      }
    } catch (error: any) {
      setResults((prev) => ({
        ...prev,
        [job.id]: {
          success: false,
          message: error.message || 'An error occurred',
        },
      }));
    } finally {
      setLoading(null);
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: 'white' }}>
      <Typography
        variant="h2"
        gutterBottom
        sx={{
          mb: 3,
          fontFamily: '"Impact", "Arial Black", sans-serif',
          fontWeight: 900,
          fontSize: '2.5rem',
          textTransform: 'uppercase',
          color: '#1a1a1a',
        }}
      >
        Cron Jobs Manager
      </Typography>
      
      <Alert
        severity="info"
        sx={{
          mb: 3,
          borderRadius: 0,
          border: '2px solid #2196f3',
          bgcolor: '#e3f2fd',
        }}
      >
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>ℹ️ Testing Mode:</strong> {process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost' ? 'Development (Local Testing)' : 'Production (Live API)'}
        </Typography>
        <Typography variant="body2">
          • In <strong>development</strong>: Uses local testing functions (no OpenAI, no emails)<br/>
          • In <strong>production</strong>: Uses real Vercel API endpoints with OpenAI and email delivery
        </Typography>
      </Alert>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {cronJobs.map((job) => (
          <Card
            key={job.id}
            sx={{
              borderRadius: 0,
              border: '3px solid #dc143c',
              bgcolor: '#1a1a1a',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      bgcolor: '#dc143c',
                      color: 'white',
                      p: 2,
                      borderRadius: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {job.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontFamily: '"Arial Black", sans-serif',
                        color: 'white',
                        textTransform: 'uppercase',
                      }}
                    >
                      {job.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#ccc' }}>
                      {job.description}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  startIcon={loading === job.id ? <CircularProgress size={20} color="inherit" /> : <RunIcon />}
                  onClick={() => handleRunCronJob(job)}
                  disabled={loading === job.id}
                  sx={{
                    bgcolor: '#dc143c',
                    color: 'white',
                    borderRadius: 0,
                    fontFamily: '"Arial Black", sans-serif',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    '&:hover': {
                      bgcolor: '#b01030',
                    },
                    '&:disabled': {
                      bgcolor: '#666',
                      color: '#999',
                    },
                    minWidth: 140,
                    py: 1.5,
                  }}
                >
                  {loading === job.id ? 'Running...' : 'Run Now'}
                </Button>
              </Box>

              {results[job.id] && (
                <Alert
                  severity={results[job.id].success ? 'success' : 'error'}
                  icon={results[job.id].success ? <SuccessIcon /> : <ErrorIcon />}
                  sx={{
                    mt: 2,
                    borderRadius: 0,
                    border: results[job.id].success ? '2px solid #4caf50' : '2px solid #f44336',
                  }}
                >
                  {results[job.id].message}
                </Alert>
              )}

              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`Endpoint: ${job.endpoint}`}
                  size="small"
                  sx={{
                    borderRadius: 0,
                    bgcolor: '#333',
                    color: 'white',
                    border: '1px solid #666',
                  }}
                />
                {job.id === 'generate-content' && (
                  <Chip
                    label="Schedule: Mon & Thu at 10 AM"
                    size="small"
                    sx={{
                      borderRadius: 0,
                      bgcolor: '#dc143c',
                      color: 'white',
                      fontWeight: 700,
                    }}
                  />
                )}
                {job.id === 'send-newsletter' && (
                  <Chip
                    label="Schedule: Daily at 8 AM"
                    size="small"
                    sx={{
                      borderRadius: 0,
                      bgcolor: '#dc143c',
                      color: 'white',
                      fontWeight: 700,
                    }}
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Card sx={{ mt: 3, bgcolor: '#fff3cd', borderRadius: 0, border: '2px solid #ffc107' }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontFamily: '"Arial Black", sans-serif',
              textTransform: 'uppercase',
              color: '#1a1a1a',
            }}
          >
            Configuration Required
          </Typography>
          <Typography variant="body2" sx={{ color: '#1a1a1a', mb: 2 }}>
            To use this feature, you need to configure the following:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <Typography component="li" variant="body2" sx={{ color: '#1a1a1a', mb: 1 }}>
              Set <code style={{ backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '2px' }}>REACT_APP_CRON_SECRET</code> in your <code style={{ backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '2px' }}>.env.local</code> file
            </Typography>
            <Typography component="li" variant="body2" sx={{ color: '#1a1a1a', mb: 1 }}>
              The secret must match the <code style={{ backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '2px' }}>CRON_SECRET</code> configured in Vercel
            </Typography>
            <Typography component="li" variant="body2" sx={{ color: '#1a1a1a', mb: 1 }}>
              OpenAI API key must be configured for content generation
            </Typography>
            <Typography component="li" variant="body2" sx={{ color: '#1a1a1a' }}>
              SMTP must be configured in Supabase for newsletter sending
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CronJobsManager;